// A booking's payment link (/secure/pay/booking/<payment-id>): the order a
// school's dashboard built for a booking (the booking itself, a deposit's
// balance, one instalment), paid by the customer. v1 loads the order into
// its basket and hands over to the basket checkout; the element pays it in
// place, with the same calls that checkout makes for it. Pure.
//
// Endpoints: GET /app/pages/secure-payment/booking/<id> (the order),
// POST …/payment-option (deposit or in full), then the basket checkout's
// /widget/app/createBasketCheckoutSession with `existingBasketOrderId`
// (the API reuses the pre-built order and its reserved seat), and
// /widget/app/processBasketPayment once Stripe has the money.

import { isOtherSchool, isOtherSchoolSlug } from "../../school";
import { formatClock, formatShortDate } from "../reschedule/sessions";
import { isValidDate } from "../../dates";

export type PayBookingStatus = "PENDING" | "SUCCEEDED" | "CANCELLED";

export type PartnerPaymentOptions = { depositPence: number; totalPence: number; selected: "deposit" | "full" };

/** One line of the order, in the basket's own shape (the dashboard built it
 * as the widget's basket would have). */
export type PartnerItem = {
  id: string;
  type: string;
  schoolId: string;
  schoolName?: string;
  courseId?: string;
  courseName?: string;
  sessionId?: string;
  sessionDate?: string | null;
  sessionEndDate?: string | null;
  ticketSelections?: Array<{ ticketTypeId: string; ticketTypeName: string; quantity: number; price: number }>;
  quantity: number;
  price: number;
  currency: string;
  bookingType?: string;
  isOnline?: boolean;
  [key: string]: unknown;
};

export type PayBookingPage = {
  status: PayBookingStatus;
  basketOrderId: string;
  paymentOptions?: PartnerPaymentOptions | null;
  school: { name: string; logoUrl: string | null; timezone: string | null; slug: string | null };
  buyer: { email: string; firstName: string; lastName: string; phone: string; fullName: string };
  items: PartnerItem[];
  totalAmount: number;
  currency: string;
  session: { title: string | null; startDateTime: string | null };
};

/** The fields of createBasketCheckoutSession's answer the page reads. */
export type CheckoutSessionResponse = {
  basketOrderId: string;
  amount?: number;
  currency?: string;
  clientSecret?: string | null;
  checkoutUrl?: string | null;
  usedStripeHostedPage?: boolean;
  isRequestToBook?: boolean;
  requestDeposit?: boolean;
  paymentMethodType?: string;
  schoolAccountID?: string;
  stripePublicKey?: string;
};

export type Buyer = { firstName: string; lastName: string; email: string; phone: string };

/** Whether the order is another school's than the site's. */
export function isOtherSchoolsOrder(businessId: string, businessSlug: string, page: PayBookingPage): boolean {
  return (
    isOtherSchool(businessId, ...(page.items ?? []).map((item) => item.schoolId)) ||
    isOtherSchoolSlug(businessSlug, page.school?.slug)
  );
}

/** What the order's lines charge: Σ price × quantity (fees come on top, in
 * the checkout's answer). */
export function itemsCharge(items: PartnerItem[]): number {
  return (items ?? []).reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0);
}

/** A line as the page lists it. */
export function lineOf(item: PartnerItem, timezone: string | null) {
  const tickets = (item.ticketSelections ?? [])
    .filter((t) => t.quantity > 0)
    .map((t) => `${t.quantity} × ${t.ticketTypeName}`)
    .join(", ");
  return {
    title: item.courseName || "Booking",
    when: item.sessionDate ? sessionWhen(item.sessionDate, item.sessionEndDate ?? null, timezone) : null,
    tickets,
    amount: (Number(item.price) || 0) * (Number(item.quantity) || 1),
  };
}

/** "Mon 5th March, 14:30" or "Mon 5th March, 14:30 - 16:00" (24-hour), in
 * the school's timezone. */
export function sessionWhen(start: string | null, end: string | null, timezone: string | null): string | null {
  if (!isValidDate(start)) return null;
  const tz = timezone ?? "";
  const from = `${formatShortDate(start, tz)}, ${formatClock(start, tz)}`;
  return isValidDate(end) ? `${from} - ${formatClock(end, tz)}` : from;
}

/** The deposit-or-in-full choice's two options (v1's PayInFullChoice). */
export function describePaymentOptions(opts: PartnerPaymentOptions, money: (pence: number) => string) {
  const remainder = Math.max(0, opts.totalPence - opts.depositPence);
  return [
    { value: "deposit" as const, title: `Pay the deposit now — ${money(opts.depositPence)}`, detail: `${money(remainder)} to pay later` },
    { value: "full" as const, title: `Pay in full — ${money(opts.totalPence)}`, detail: "Nothing more to pay" },
  ];
}

/** The order's lines as the checkout takes them: the fields v1's basket
 * sends (buildBasketItemsPayload), straight from the order's snapshot — the
 * API refuses a submission whose lines differ from it. */
export function checkoutItems(items: PartnerItem[], buyer: Buyer) {
  return items.map((item) => ({
    id: item.id,
    type: item.type,
    schoolId: item.schoolId,
    courseId: item.courseId,
    sessionId: item.sessionId,
    productId: item.productId,
    redeemedFromBundleId: item.redeemedFromBundleId,
    productSlug: item.productSlug,
    ticketSelections: item.ticketSelections,
    addOns: item.addOns,
    quantity: item.quantity,
    price: item.price,
    currency: item.currency,
    courseName: item.courseName,
    productName: item.productName,
    sessionDate: item.sessionDate,
    sessionEndDate: item.sessionEndDate,
    imageUrl: item.imageUrl,
    bookingType: item.bookingType,
    checkoutGroupId: item.checkoutGroupId,
    isOnline: item.isOnline,
    recipientEmail: item.recipientEmail,
    recipientName: item.recipientName,
    senderName: item.senderName || `${buyer.firstName} ${buyer.lastName}`,
    giftCardMessage: item.message,
    giftCardAmount: item.giftcardAmount,
    selectedImage: item.imageUrl,
    postageId: item.postageId,
    isGift: item.isGift,
    giftingDetails: item.giftingDetails,
    termData: item.termData,
    appointmentData: item.appointmentData,
    userInputs: item.userInputs,
    participants: item.participants,
    checkoutGuestsInformation: item.checkoutGuestsInformation,
    bundleTemplateId: item.bundleTemplateId,
    bundleName: item.bundleName,
  }));
}

/** createBasketCheckoutSession's body for this order. `returnUrl` is this
 * page on this host (with the link query): where a hosted Checkout comes
 * back to. */
export function checkoutBody(args: {
  page: PayBookingPage;
  buyer: Buyer;
  sourceInfo: Record<string, unknown>;
  returnUrl: string;
}) {
  const { page, buyer } = args;
  return {
    items: checkoutItems(page.items, buyer),
    buyer: {
      email: buyer.email.trim(),
      firstName: buyer.firstName.trim(),
      lastName: buyer.lastName.trim(),
      phone: buyer.phone.trim(),
      marketingOptIn: false,
    },
    source: "dashboard-partner-initiated",
    sourceInfo: args.sourceInfo,
    existingBasketOrderId: page.basketOrderId,
    returnUrl: args.returnUrl,
  };
}

/** Whether a refused checkout means the order moved on (paid, cancelled,
 * gone, or re-priced since the page loaded): then the page re-reads it. */
export function isStaleOrderError(status: number, message: string): boolean {
  if (status >= 500) return false;
  return /cannot be paid for|not a partner-initiated|basket order not found|no longer matches|has changed since you opened it/i.test(message);
}
